import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import BarChartHalf from './BarChartHalf';

import LineChartDemos from './LineChartDemos';

import StackkedChartNarratives from './StackkedChartNarrative';

import LineChartBooking from './LineChartBooking';

import RegionalSENarrativeBreakdown from './RegionalSENarrativeBreakdown';

import PieChart from './PieChart';

import StackedHorizontalBarChart from './StackedHorizontalBarChart';

import RegionalIngestionOverview from './RegionalIngestionOverview';
import { fetchLeadershipInternalSEAnalytics } from '../../../../Redux/Constants/apiService';
import { mapInternalSEAnalyticsResponse } from './internalSEAnalyticsMapper';

import { INTERNAL_SE_SOFT_BLUE_COLORS } from './chartTheme';

const PERIOD_FILTER_MAP = {
  Week: 'Week',
  LastWeek: 'LastWeek',
  Month: 'Month',
  LastMonth: 'LastMonth',
  Quarter: 'Quarter',
  LastQuarter: 'LastQuarter',
  Year: 'Year',
  LastYear: 'LastYear',
};

const REGION_FILTER_MAP = {
  all: 'all',
  APJ: 'APJ',
  EMEA: 'EMEA',
  NA: 'NA',
  LATAM: 'LATAM',
};
const INTERNAL_REGIONS = ['APJ', 'EMEA', 'NA', 'LATAM'];
const WEEK_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FISCAL_MONTH_LABELS = [
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
  'Mar',
];

const API_ERROR_MESSAGE = 'Unable to load analytics. Please try again.';
const EMPTY_INTERNAL_SE_DATA = {
  regionalOverview: {},
  topSEsStacked: { names: [], series: [] },
  runDistributionByRegion: {},
  narrativeTrendsPerRegion: { xData: [], series: [] },
  regionalNarrativeBreakdown: { xData: [], series: [], total: [] },
  regionalIngestion: {},
  topNarrativesGlobally: {},
  bookingVolumePerRegion: { xData: [], series: [] },
  regionalSENarrativeBreakdown: {},
};
const formatAverageRuns = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.round(numericValue);
};

const getQuarterMonthLabels = (period) => {
  const now = new Date();
  const month = now.getMonth();
  const currentQuarterStart = Math.floor(month / 3) * 3;
  const quarterStart = period === 'LastQuarter'
    ? (currentQuarterStart + 9) % 12
    : currentQuarterStart;
  return [0, 1, 2].map((offset) =>
    new Date(now.getFullYear(), quarterStart + offset, 1).toLocaleString('en-US', { month: 'short' })
  );
};

const buildZeroTrendData = (filter) => {
  if (filter === 'Week' || filter === 'LastWeek') return WEEK_DAY_LABELS;
  if (filter === 'Quarter' || filter === 'LastQuarter') return getQuarterMonthLabels(filter);
  if (filter === 'Year' || filter === 'LastYear') return FISCAL_MONTH_LABELS;
  return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
};



const NarrativeRegionCard = ({ region, narratives, avgRuns }) => (

  <div className="col-lg-3 col-md-6 col-12">

    <div className="lead-dash-region-card">

      <div className="lead-dash-region-card-header">{region}</div>

      <div className="lead-dash-region-card-body">

        <h4 className="lead-dash-region-card-subtitle">Top 3 Narratives</h4>

        <ul className="lead-dash-region-list">
          {narratives.length > 0 ? (
            narratives.map((narrative, index) => (
              <li key={narrative}>
                <span
                  className="lead-narrative-bullet"
                  style={{
                    backgroundColor:
                      INTERNAL_SE_SOFT_BLUE_COLORS[index % INTERNAL_SE_SOFT_BLUE_COLORS.length],
                  }}
                />
                <span>{narrative}</span>
              </li>
            ))
          ) : (
            <li>-</li>
          )}

        </ul>

      </div>

      <div className="lead-dash-region-card-footer">

        <span className="lead-dash-region-metric-label">Avg Narrative Runs Per SE</span>

        <span className="lead-dash-region-metric-value">{formatAverageRuns(avgRuns)}</span>

      </div>

    </div>

  </div>

);



const InternalSEAnalyticsSection = ({ period, onWarningsChange, onLoadingChange }) => {
  const accessToken = useSelector((state) => state.auth?.accessToken);
  const [data, setData] = useState(EMPTY_INTERNAL_SE_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadInternalAnalytics = async () => {
      setIsLoading(true);
      setErrorMessage('');

      if (!accessToken) {
        if (!isMounted) return;
        setData(EMPTY_INTERNAL_SE_DATA);
        setErrorMessage(API_ERROR_MESSAGE);
        onWarningsChange?.([]);
        setIsLoading(false);
        return;
      }

      try {
        const mappedPeriod = PERIOD_FILTER_MAP[period] || 'Month';
        const mappedRegion = REGION_FILTER_MAP.all;
        const response = await fetchLeadershipInternalSEAnalytics(accessToken, mappedPeriod, mappedRegion);

        if (!response?.success) {
          throw new Error(response?.message || 'Internal SE analytics API returned an unsuccessful response.');
        }

        const mappedData = mapInternalSEAnalyticsResponse(response?.internalSEAnalytics || {});
        const warnings = Array.isArray(response?.meta?.warnings)
          ? response.meta.warnings.filter(Boolean)
          : [];

        if (!isMounted) return;
        setData(mappedData);
        setErrorMessage('');
        onWarningsChange?.(warnings);
      } catch (_error) {
        if (!isMounted) return;
        setData(EMPTY_INTERNAL_SE_DATA);
        setErrorMessage(API_ERROR_MESSAGE);
        onWarningsChange?.([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadInternalAnalytics();

    return () => {
      isMounted = false;
    };
  }, [accessToken, onWarningsChange, period]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    return () => {
      onWarningsChange?.([]);
    };
  }, [onWarningsChange]);

  if (isLoading) {
    return (
      <div className="lead-dash-card lead-dash-card-full">
        <div className="lead-dash-card-body">
          <div className="lead-dash-no-data lead-partner-no-data">Loading Internal SE analytics...</div>
        </div>
      </div>
    );
  }

  const regionalOverviewEntries = Object.entries(data?.regionalOverview || {});
  const normalizedRegionalOverviewEntries = regionalOverviewEntries.length
    ? regionalOverviewEntries
    : INTERNAL_REGIONS.map((region) => [region, { topNarratives: [], avgRunsPerSE: 0 }]);
  const zeroTrendXAxis = buildZeroTrendData(period);

  return (
    <div className="lead-dash-content">
      {errorMessage && (
        <div className="lead-dash-inline-error" role="alert">
          {errorMessage}
        </div>
      )}

      <div id="export-internal-global-narrative-volumes" className="lead-dash-card lead-dash-card-full">
        <div className="lead-dash-card-header">
          <h3 className="lead-dash-card-title">Global Narrative Volumes &amp; Trends</h3>
        </div>

        <div className="lead-dash-card-body">

          <div className="row g-3">
            {normalizedRegionalOverviewEntries.map(([regionName, info]) => (
              <NarrativeRegionCard
                key={regionName}
                region={regionName}
                narratives={info?.topNarratives || []}
                avgRuns={info?.avgRunsPerSE || 0}
              />
            ))}

          </div>

        </div>

      </div>



      <div id="export-internal-top-ses-globally" className="lead-dash-card lead-dash-card-full">
        <div className="lead-dash-card-header">
          <h3 className="lead-dash-card-title">Top SE&apos;s Globally</h3>
        </div>

        <div className="lead-dash-card-body">

          <div className="row g-4 align-items-stretch">

            <div className="col-lg-7 col-12">

              <StackedHorizontalBarChart
                data={data.topSEsStacked}
                renderZeroWhenEmpty={!errorMessage}
                emptyStateTitle="No activity for this period"
                emptyStateSubtitle="0 narrative runs found for the selected filter."
              />

            </div>

            <div className="col-lg-5 col-12">

              <h4 className="lead-dash-chart-subtitle">Run Distribution by Region</h4>

              <PieChart
                data={data.runDistributionByRegion}
                donut
                renderZeroWhenEmpty={!errorMessage}
                zeroRegions={['APJ', 'EMEA', 'NA', 'LATAM']}
                emptyStateTitle="No activity for this period"
                emptyStateSubtitle="0 narrative runs found for the selected filter."
              />

            </div>

          </div>

        </div>

      </div>



      <div className="lead-dash-grid-2">

        <div id="export-internal-narrative-trends-region" className="lead-dash-card">
          <div className="lead-dash-card-header">
            <h3 className="lead-dash-card-title">Narrative Volumes &amp; Trends per Region</h3>
          </div>

          <div className="lead-dash-card-body lead-dash-chart-body">

            <LineChartDemos
              data={data.narrativeTrendsPerRegion}
              range={period}
              display=""
              renderZeroWhenEmpty={!errorMessage}
              zeroXAxis={zeroTrendXAxis}
              zeroSeriesNames={['APJ', 'EMEA', 'NA', 'LATAM']}
              emptyStateTitle="No activity for this period"
              emptyStateSubtitle="0 narrative runs found for the selected filter."
            />

          </div>

        </div>

        <div id="export-internal-regional-narratives-breakdown" className="lead-dash-card">
          <div className="lead-dash-card-header">
            <h3 className="lead-dash-card-title">Regional Narratives Breakdown</h3>
          </div>

          <div className="lead-dash-card-body lead-dash-chart-body">

            <StackkedChartNarratives
              data={data.regionalNarrativeBreakdown}
              renderZeroWhenEmpty={!errorMessage}
              zeroCategories={['APJ', 'EMEA', 'NA', 'LATAM']}
              zeroSeriesNames={['Narrative Runs']}
              emptyStateTitle="No activity to show"
              emptyStateSubtitle="Try another company or time period."
            />

          </div>

        </div>

      </div>



      <div id="export-internal-regional-ingestion-overview" className="lead-dash-card lead-dash-card-full">
        <div className="lead-dash-card-header">
          <h3 className="lead-dash-card-title">Regional Ingestion Type Overview</h3>
        </div>

        <div className="lead-dash-card-body">

          <RegionalIngestionOverview
            data={data.regionalIngestion}
            renderZeroWhenEmpty={!errorMessage}
            emptyStateTitle="No ingestion activity"
            emptyStateSubtitle="0 runs found for the selected filter."
          />

        </div>

      </div>



      <div className="lead-dash-grid-2">

        <div id="export-internal-top-narratives" className="lead-dash-card">
          <div className="lead-dash-card-body lead-dash-chart-body pt-0">
            <BarChartHalf
              rawData={data.topNarrativesGlobally}
              embedded
              renderZeroWhenEmpty={!errorMessage}
              emptyStateTitle="No activity for this period"
              emptyStateSubtitle="0 narrative runs found for the selected filter."
            />
          </div>
        </div>

        <div id="export-internal-booking-volume-region" className="lead-dash-card">
          <div className="lead-dash-card-header">
            <h3 className="lead-dash-card-title">Booking Volume per Region</h3>
          </div>

          <div className="lead-dash-card-body lead-dash-chart-body">
            <LineChartBooking
              data={data.bookingVolumePerRegion}
              range={period}
              display=""
              renderZeroWhenEmpty={!errorMessage}
              zeroXAxis={zeroTrendXAxis}
              zeroSeriesNames={['APJ', 'EMEA', 'NA', 'LATAM']}
              emptyStateTitle="No bookings for this period"
              emptyStateSubtitle="0 bookings found for the selected filter."
            />
          </div>
        </div>

      </div>



      <RegionalSENarrativeBreakdown
        exportId="export-internal-regional-se-narrative-breakdown"
        data={data.regionalSENarrativeBreakdown}
        renderZeroWhenEmpty={!errorMessage}
        zeroRegion="APJ"
        emptyStateTitle="No activity to show"
        emptyStateSubtitle="Try another company or time period."
      />

    </div>

  );

};



export default InternalSEAnalyticsSection;


